import { Loadable } from '@/components/loaders';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { useGetAudienceQuery } from '@/services/endpoints/audiences';
import { usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import Form, { IChangeEvent } from '@rjsf/core';
import { RJSFSchema, type WidgetProps } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS } from '@/features/destinations/components/connectorFormConfig';
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowFormData, FlowMappingsConfig, ResolveStepAutoFill, StepConfig } from './types';
import { templates, widgets } from './FormFields/StyledField';
import ErrorBoundary from '@/components/ErrorBoundary';
import { EmailTemplatePreview } from '../Email/TemplatePreview/EmailTemplatePreview';
import { SmsTemplatePreview } from '../Sms/TemplatePreview/SmsTemplatePreview';
import { TemplatePreview } from './TemplatePreview/TemplatePreview';
import { Button } from '@/lib';
import { SpinnerIcon } from '@/lib/Spinner';
import { StepsIndication } from '@/components/StepsIndication/StepsIndication';
import { enhanceFlowStepForHtmlBodyMode, TEMPLATE_BODY_HTML_FIELD, type HtmlBodyFieldMode } from './enhanceFlowStepUiSchema';
import { getWhatsappExamplesMismatch } from './templateVariableExamples';
import type { FormValidation } from '@rjsf/utils';

type RjsfFormInstance = InstanceType<typeof Form>;

const reportStepValidity = (formData: FlowFormData, schema: RJSFSchema | undefined) => {
  if (!schema) {
    return { isSchemaValid: false, errorsLength: 1 };
  }
  const { errors } = validator.validateFormData(formData, schema);
  return { isSchemaValid: errors.length === 0, errorsLength: errors.length };
};

type StepFormSchema = NonNullable<StepConfig['reactJsonSchemaForm']>;

type MultiStepFlowFormProps = {
  id?: string;
  audienceId: string;
  connectorInstanceId: string;
  connectorFlowId: string;
  paramsConfig: Record<string, StepConfig>;
  /** Prefill per step (chiave = stepN). I campi hidden con default dal backend vincono solo se assenti nel seed. */
  initialFormData?: Record<string, FlowFormData>;
  /**
   * Optional channel/flow-owned auto-fill. When it returns `{ skip: true }`, the step is
   * advanced with `data` merged into accumulated params (same path as all-hidden steps).
   */
  resolveStepAutoFill?: ResolveStepAutoFill;
  /**
   * Optional post-prepare transform (e.g. Comuni topic-filter of template enums).
   * Applied after the live step schema is resolved and before auto-fill / render.
   */
  transformStepFormSchema?: (formSchema: StepFormSchema) => StepFormSchema;
  /** Extra RJSF widgets merged over the default StyledField set (e.g. Comuni locked name). */
  extraWidgets?: Record<string, FC<WidgetProps>>;
  onComplete: (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => void;
  showTemplatePreview?: boolean;
  templatePreviewVariant?: 'whatsapp' | 'email' | 'sms';
  isInAction?: boolean;
  htmlBodyFieldMode?: HtmlBodyFieldMode;
  lastStepActionLabel?: string;
};

const isSchemaAllHidden = (form: { schema?: RJSFSchema; uiSchema?: Record<string, unknown> } | undefined): boolean => {
  const props = (form?.schema as any)?.properties || {};
  const keys = Object.keys(props);
  if (keys.length === 0) return true;
  const ui = (form?.uiSchema as Record<string, any>) || {};
  return keys.every((k) => ui[k]?.['ui:widget'] === 'hidden');
};

const collectDefaults = (schema: RJSFSchema | undefined): FlowFormData => {
  const data: FlowFormData = {};
  const props = (schema as any)?.properties || {};
  for (const [key, prop] of Object.entries<any>(props)) {
    if (prop?.default !== undefined) data[key] = prop.default;
  }
  return data;
};

export const MultiStepFlowForm: FC<MultiStepFlowFormProps> = ({
  audienceId,
  connectorFlowId,
  connectorInstanceId,
  paramsConfig,
  initialFormData,
  resolveStepAutoFill,
  transformStepFormSchema,
  extraWidgets,
  onComplete,
  id = 'multi-step-flow',
  showTemplatePreview = false,
  templatePreviewVariant = 'whatsapp',
  isInAction = false,
  htmlBodyFieldMode = 'default',
  lastStepActionLabel,
}) => {
  const { t } = useTranslation();
  const formWidgets = useMemo(() => (extraWidgets ? { ...widgets, ...extraWidgets } : widgets), [extraWidgets]);
  const { t: tCampaign } = useTranslation('campaign');
  const { id: workspaceId } = useWorkspace();
  const [prepareStep, { isLoading: isLoadingStep }] = usePrepareConnectorFlowStepMutation();
  const dispatch = useAppDispatch();
  const { data: audience, isLoading: isAudienceLoading } = useGetAudienceQuery(
    { workspaceId, audienceId },
    {
      skip: !workspaceId || !audienceId,
    }
  );

  const isAudienceQueryPending = !!audienceId && isAudienceLoading;

  const { currentLanguage } = useCompanyValues();

  // Get sorted step keys (step1, step2, step3, ...)
  const stepKeys = useMemo(() => {
    return Object.keys(paramsConfig)
      .filter((key) => /^step\d+$/.test(key))
      .sort((a, b) => parseInt(a.replace('step', '')) - parseInt(b.replace('step', '')));
  }, [paramsConfig]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentStepSchema, setCurrentStepSchema] = useState<{
    schema: RJSFSchema;
    uiSchema: Record<string, unknown>;
    title?: string;
  } | null>(null);
  const [currentFormData, setCurrentFormData] = useState<FlowFormData>({});
  const [accumulatedData, setAccumulatedData] = useState<FlowFormData>({});
  const [stepDataByKey, setStepDataByKey] = useState<Record<string, FlowFormData>>(initialFormData ?? {});
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastStepMappings, setLastStepMappings] = useState<FlowMappingsConfig>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [autoSkippedSteps, setAutoSkippedSteps] = useState<Set<string>>(new Set());
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const hasInitialized = useRef(false);
  const activeRequestId = useRef(0);
  const pendingAutoFillStep = useRef<{ stepIndex: number; dataToSend: FlowFormData } | null>(null);
  const seededStepKeys = useRef(new Set(Object.keys(initialFormData ?? {})));
  const resolveStepAutoFillRef = useRef(resolveStepAutoFill);
  resolveStepAutoFillRef.current = resolveStepAutoFill;
  const transformStepFormSchemaRef = useRef(transformStepFormSchema);
  transformStepFormSchemaRef.current = transformStepFormSchema;
  const formRef = useRef<RjsfFormInstance | null>(null);

  const isLastStep = currentStepIndex === stepKeys.length - 1;
  const hasPreviousStep = stepKeys.slice(0, currentStepIndex).some((key) => !autoSkippedSteps.has(key));
  const enforceWhatsappExamples = templatePreviewVariant === 'whatsapp';
  const examplesMismatch = enforceWhatsappExamples ? getWhatsappExamplesMismatch(currentFormData) : null;
  const examplesOk = !examplesMismatch;

  const whatsappExamplesCustomValidate = useMemo(() => {
    if (!enforceWhatsappExamples) return undefined;
    return (formData: any, errors: FormValidation) => {
      const mismatch = getWhatsappExamplesMismatch(formData as FlowFormData);
      if (mismatch?.body && errors['body_examples']) {
        errors['body_examples'].addError(
          String(tCampaign('whatsappTemplates.examplesRequiredBody', { count: mismatch.body.required }))
        );
      }
      if (mismatch?.header && errors['header_examples']) {
        errors['header_examples'].addError(
          String(tCampaign('whatsappTemplates.examplesRequiredHeader', { count: mismatch.header.required }))
        );
      }
      return errors;
    };
  }, [enforceWhatsappExamples, tCampaign]);

  const evaluateFormValid = useCallback(
    (formData: FlowFormData, schemaErrorsLength: number) => {
      const mismatch = enforceWhatsappExamples ? getWhatsappExamplesMismatch(formData) : null;
      return schemaErrorsLength === 0 && !mismatch;
    },
    [enforceWhatsappExamples]
  );

  const computeFormValid = useCallback(
    (formData: FlowFormData, schema: RJSFSchema | undefined) => {
      const { errorsLength } = reportStepValidity(formData, schema);
      return evaluateFormValid(formData, errorsLength);
    },
    [evaluateFormValid]
  );

  const loadStepSchema = async (stepIndex: number, dataToSend: FlowFormData): Promise<void> => {
    const stepIdToCall = stepKeys[stepIndex];
    const bodyToSend = stepIndex === 0 ? {} : dataToSend;
    const requestId = ++activeRequestId.current;
    setStepError(null);

    const staticFormId = paramsConfig[stepIdToCall]?.reactJsonSchemaForm?.formId;
    if (AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(staticFormId)) {
      if (!audience && isAudienceQueryPending) {
        pendingAutoFillStep.current = { stepIndex, dataToSend };
        return;
      }
      pendingAutoFillStep.current = null;
      const autoFillData = { audience_name: audience?.name ?? '', createdOn: audience?.createdOn ?? '' };
      const newAccumulatedData = { ...dataToSend, ...autoFillData };
      setAccumulatedData(newAccumulatedData);

      if (stepIndex === stepKeys.length - 1) {
        onComplete(newAccumulatedData, null);
      } else {
        const nextStepIndex = stepIndex + 1;
        setCurrentStepIndex(nextStepIndex);
        setCurrentFormData({});
        await loadStepSchema(nextStepIndex, newAccumulatedData);
      }
      return;
    }

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: connectorFlowId,
        stepId: stepIdToCall,
        audienceId: audienceId,
        connectorInstanceId,
        stepData: bodyToSend,
      }).unwrap();

      if (requestId !== activeRequestId.current) {
        return;
      }

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      const preparedStepConfig = translatedResponse?.configuration?.elements?.params?.[stepKeys[stepIndex]];
      const rawFormSchema =
        preparedStepConfig?.reactJsonSchemaForm ||
        translatedResponse?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.reactJsonSchemaForm ||
        translatedParamsConfig[stepKeys[stepIndex]]?.reactJsonSchemaForm;
      const formSchema = rawFormSchema
        ? (transformStepFormSchemaRef.current?.(rawFormSchema) ?? rawFormSchema)
        : rawFormSchema;

      /**
       * Mappings RJSF config from `prepare` (POST): try in order until `reactJsonSchemaForm` is found.
       * Keep in sync with FlowMappingsForm / TemplateSelectionStep after the flow completes.
       *
       * 1. `configuration.elements.mappings.reactJsonSchemaForm`
       * 2. `configuration.mappings.reactJsonSchemaForm`
       * 3. `mappings.reactJsonSchemaForm`
       */
      const mappingsSchema =
        translatedResponse?.configuration?.elements?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.mappings?.reactJsonSchemaForm;
      const currentMappings: FlowMappingsConfig = mappingsSchema?.schema ? { schema: mappingsSchema.schema, uiSchema: mappingsSchema.uiSchema || {} } : null;
      if (currentMappings) {
        setLastStepMappings(currentMappings);
      }

      if (formSchema) {
        if (AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(formSchema.formId)) {
          if (!audience && isAudienceQueryPending) {
            pendingAutoFillStep.current = { stepIndex, dataToSend };
            return;
          }
          pendingAutoFillStep.current = null;
          const autoFillData = { audience_name: audience?.name ?? '', createdOn: audience?.createdOn ?? '' };
          const newAccumulatedData = { ...dataToSend, ...autoFillData };
          setAccumulatedData(newAccumulatedData);

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        // Auto-skip steps that have only hidden fields (or no fields at all).
        // Hidden defaults come from the backend (addHiddenFieldToSchema), so we just
        // collect them and advance — no UI for the user to interact with.
        if (isSchemaAllHidden(formSchema)) {
          const autoData = collectDefaults(formSchema.schema);
          const newAccumulatedData = { ...dataToSend, ...autoData };
          setAccumulatedData(newAccumulatedData);
          setAutoSkippedSteps((prev) => new Set(prev).add(stepIdToCall));

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        // Channel/flow-owned auto-fill (e.g. WhatsApp Customize pre-selects the template enum).
        const autoFill = resolveStepAutoFillRef.current?.({
          stepId: stepIdToCall,
          stepIndex,
          formSchema,
          accumulatedData: dataToSend,
        });
        if (autoFill?.skip) {
          const newAccumulatedData = { ...dataToSend, ...autoFill.data };
          setAccumulatedData(newAccumulatedData);
          setStepDataByKey((prev) => ({ ...prev, [stepIdToCall]: autoFill.data }));
          setAutoSkippedSteps((prev) => new Set(prev).add(stepIdToCall));

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        const { schema: stepSchema, uiSchema: stepUiSchema } = enhanceFlowStepForHtmlBodyMode(
          (formSchema.uiSchema || {}) as Record<string, unknown>,
          formSchema.schema,
          htmlBodyFieldMode
        );
        const resolvedSchema = stepSchema ?? formSchema.schema;
        const enhancedFormSchema = { schema: resolvedSchema, uiSchema: stepUiSchema };

        // After hiding template_body_html for the external designer, a step may become
        // all-hidden — collect defaults (including body HTML) and advance.
        if (htmlBodyFieldMode === 'external' && isSchemaAllHidden(enhancedFormSchema)) {
          const autoData = {
            ...collectDefaults(resolvedSchema),
            ...(stepDataByKey[stepIdToCall] ?? {}),
          };
          const newAccumulatedData = { ...dataToSend, ...autoData };
          setAccumulatedData(newAccumulatedData);
          setAutoSkippedSteps((prev) => new Set(prev).add(stepIdToCall));

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        setCurrentStepSchema({
          schema: resolvedSchema,
          uiSchema: stepUiSchema,
          title: formSchema.title,
        });
        setShowValidationErrors(false);
        const currentStepKey = stepKeys[stepIndex];
        const stored = stepDataByKey[currentStepKey];
        // Merge schema defaults under the data only for steps seeded via initialFormData:
        // hidden backend-injected fields (e.g. waba_id) must survive an untouched submit.
        // When the HTML body is owned by the external designer, inject only template_body_html
        // from schema defaults so connector-provided body reaches onComplete / the designer.
        // Non-seeded non-external steps keep the exact pre-existing semantics (stored ?? {}).
        const isSeededStep = seededStepKeys.current.has(currentStepKey);
        let existingData: FlowFormData;
        if (isSeededStep) {
          existingData = { ...collectDefaults(resolvedSchema), ...(stored ?? {}) };
        } else if (htmlBodyFieldMode === 'external') {
          const defaults = collectDefaults(resolvedSchema);
          const bodyDefault = defaults[TEMPLATE_BODY_HTML_FIELD];
          existingData = {
            ...(typeof bodyDefault === 'string' ? { [TEMPLATE_BODY_HTML_FIELD]: bodyDefault } : {}),
            ...(stored ?? {}),
          };
        } else {
          existingData = stored ?? {};
        }
        const withOptionalPrefill = autoFill?.data ? { ...existingData, ...autoFill.data } : existingData;
        setCurrentFormData(withOptionalPrefill);
      }
    } catch (error: unknown) {
      if (requestId !== activeRequestId.current) {
        return;
      }
      const apiError = error as { data?: { message?: string } };
      const errorMessage = apiError?.data?.message || 'Something went wrong. Could not load this step.';
      setStepError(errorMessage);
      dispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
    }
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      setIsInitializing(true);
      try {
        await loadStepSchema(0, {});
      } finally {
        setIsInitializing(false);
      }
    };
    void init();
  }, []);

  useEffect(() => {
    if (isAudienceQueryPending) return;
    if (!pendingAutoFillStep.current) return;

    const pendingStep = pendingAutoFillStep.current;
    pendingAutoFillStep.current = null;
    void loadStepSchema(pendingStep.stepIndex, pendingStep.dataToSend);
  }, [isAudienceQueryPending, audience]);

  useEffect(() => {
    if (!showValidationErrors || !formRef.current) {
      return;
    }
    formRef.current.validateForm();
  }, [showValidationErrors, currentStepSchema]);

  const handleNextStep = async () => {
    const schema = currentStepSchema?.schema;
    const isValid = computeFormValid(currentFormData, schema);
    const canProceed = isValid && examplesOk;

    if (!canProceed) {
      setShowValidationErrors(true);
      return;
    }

    const newAccumulatedData = { ...accumulatedData, ...currentFormData };
    setAccumulatedData(newAccumulatedData);
    setStepDataByKey((prev) => ({ ...prev, [stepKeys[currentStepIndex]]: currentFormData }));

    if (isLastStep) {
      onComplete(newAccumulatedData, lastStepMappings);
    } else {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextStepIndex);
      setShowValidationErrors(false);
      await loadStepSchema(nextStepIndex, newAccumulatedData);
    }
  };

  const handlePreviousStep = async () => {
    let prevStepIndex = currentStepIndex - 1;
    while (prevStepIndex >= 0 && autoSkippedSteps.has(stepKeys[prevStepIndex])) {
      prevStepIndex -= 1;
    }
    if (prevStepIndex >= 0) {
      setCurrentStepIndex(prevStepIndex);
      const stepKey = stepKeys[prevStepIndex];
      const previousData = stepDataByKey[stepKey] ?? {};
      setCurrentFormData(previousData);
      setShowValidationErrors(false);
      await loadStepSchema(prevStepIndex, accumulatedData);
    }
  };

  const previewFormData = { ...accumulatedData, ...currentFormData };
  const nextDisabled = isLoadingStep || isInAction;
  const nextLooksDisabled = isLoadingStep || isInAction;

  return (
    <div className={`w-full grid ${showTemplatePreview ? 'grid-cols-3' : 'grid-cols-1'} gap-6`}>
      <div className='space-y-6 col-span-2 border border-gray-200 rounded-2xl p-4'>
        {/* Step indicator */}
        <StepsIndication steps={stepKeys} currentStepIndex={currentStepIndex} />
        {/* Step title */}
        {currentStepSchema?.title && <h3 className='text-lg font-medium text-primary-900'>{currentStepSchema.title}</h3>}
        {stepError && <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{stepError}</div>}

        {/* Form */}
        <Loadable isLoading={isInitializing || isLoadingStep}>
          {currentStepSchema && (
            <div className='whatsapp-flow-rjsf rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
              <Form
                ref={formRef}
                className='rjsf'
                tagName='div'
                id={id}
                noHtml5Validate
                showErrorList={false}
                liveValidate={showValidationErrors}
                schema={currentStepSchema.schema}
                uiSchema={currentStepSchema.uiSchema}
                formData={currentFormData}
                templates={templates}
                widgets={formWidgets}
                customValidate={whatsappExamplesCustomValidate}
                onChange={(event: IChangeEvent) => {
                  const nextData = (event.formData as FlowFormData) ?? {};
                  setCurrentFormData(nextData);
                }}
                validator={validator}
              >
                <Fragment />
              </Form>
            </div>
          )}
        </Loadable>

        {examplesMismatch ? (
          <div className='rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800'>
            {examplesMismatch.body
              ? String(tCampaign('whatsappTemplates.examplesRequiredBody', { count: examplesMismatch.body.required }))
              : null}
            {examplesMismatch.body && examplesMismatch.header ? <br /> : null}
            {examplesMismatch.header
              ? String(tCampaign('whatsappTemplates.examplesRequiredHeader', { count: examplesMismatch.header.required }))
              : null}
          </div>
        ) : null}

        {/* Navigation buttons */}
        <div className='flex justify-between pt-4'>
          <Button
            type='button'
            intent='secondary'
            onClick={handlePreviousStep}
            disabled={!hasPreviousStep}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              !hasPreviousStep ? 'cursor-not-allowed text-gray-400' : 'text-primary-600 hover:text-primary-700'
            }`}
          >
            {String(t('COMMON:previous' as any))}
          </Button>
          <Button
            type='button'
            intent='primary'
            onClick={handleNextStep}
            disabled={nextDisabled}
            className={`text-sm font-medium ${nextLooksDisabled ? 'cursor-not-allowed bg-gray-300 text-gray-500' : ''}`}
          >
            {isInAction ? (
              <SpinnerIcon className='size-5' pathClassName='stroke-primary-icon' />
            ) : (
              <>{String(isLastStep ? (lastStepActionLabel ?? (t('COMMON:confirm' as any) as string)) : (t('COMMON:next' as any) as string))}</>
            )}
          </Button>
        </div>
      </div>
      {showTemplatePreview && templatePreviewVariant === 'email' ? (
        <ErrorBoundary>
          <EmailTemplatePreview formData={previewFormData} />
        </ErrorBoundary>
      ) : null}
      {showTemplatePreview && templatePreviewVariant === 'sms' ? (
        <ErrorBoundary>
          <SmsTemplatePreview formData={previewFormData} />
        </ErrorBoundary>
      ) : null}
      {showTemplatePreview && templatePreviewVariant !== 'email' && templatePreviewVariant !== 'sms' ? (
        <ErrorBoundary>
          <TemplatePreview formData={previewFormData} />
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
